import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import axiosInstance from '../../../../../interceptor/axiosInstance';
import './AddOneQuestion.css';

// Toast notification function
const toast = {
  success: (message) => {
    const toast = document.createElement('div');
    toast.className = 'custom-toast custom-toast-success';
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => toast.classList.add('show'), 10);
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => document.body.removeChild(toast), 300);
    }, 3000);
  },
  error: (message) => {
    const toast = document.createElement('div');
    toast.className = 'custom-toast custom-toast-error';
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => toast.classList.add('show'), 10);
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => document.body.removeChild(toast), 300);
    }, 3000);
  },
};

const AddOneQuestion = ({ onClose, loading, assessmentId, refreshData }) => {
  const [formData, setFormData] = useState({
    text: '',
    image: '',
    is_randomized: false,
    marks: 1,
    level: 'easy',
    question_type: 'mcsr',
    max_selection: 1,
    segment_id: '',
  });

  const [segments, setSegments] = useState([]); // State to store segments
  const [loadingSegments, setLoadingSegments] = useState(true); // State to track segment loading
  const [saving, setSaving] = useState(false);  // State to track saving status

  // Fetch segment data on component mount
  useEffect(() => {
    const fetchSegments = async () => {
      try {
        const response = await axiosInstance.get(`assessments/segments/?test_id=${assessmentId}`);
        if (response.status === 200) {
          setSegments(response.data?.data); // Assuming data is an array of segments
        }
      } catch (error) {
      } finally {
        setLoadingSegments(false); // Set loading to false once data is fetched
      }
    };

    fetchSegments();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };


  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setFormData({ ...formData, image: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const saveQuestion = async () => {
    try {
      const formDataToSend = new FormData();

      // Append text fields to the form data
      formDataToSend.append('text', formData.text);
      formDataToSend.append('is_randomized', formData.is_randomized);
      formDataToSend.append('marks', formData.marks);
      formDataToSend.append('level', formData.level);
      formDataToSend.append('question_type', formData.question_type);
      formDataToSend.append('max_selection', formData.max_selection);
      formDataToSend.append('segment', formData.segment_id);  // Send segment ID

      // If an image exists, append it to the form data
      if (formData.image) {
        const imageBlob = dataURItoBlob(formData.image); // Convert image data URL to Blob
        formDataToSend.append('image', imageBlob, 'image.jpg');  // Append image with filename
      }

      const response = await axiosInstance.post('assessments/questions/', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.status === 201) {
        return response.data?.data.id;  // Return the created question ID
      } else {
        throw new Error(`Failed to save question. Response status: ${response.status}`);
      }
    } catch (error) {
      throw new Error(`Error saving question: ${error.message || error}`);
    }
  };

  // Helper function to convert image data URL to Blob
  const dataURItoBlob = (dataURI) => {
    const byteString = atob(dataURI.split(',')[1]);
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const uintArray = new Uint8Array(arrayBuffer);

    for (let i = 0; i < byteString.length; i++) {
      uintArray[i] = byteString.charCodeAt(i);
    }

    return new Blob([uintArray], { type: mimeString });
  };

  const handleSave = async () => {
    setSaving(true);  // Start the saving process

    try {
      // Save the question first and get the question ID
      await saveQuestion();

      // Show success toast
      toast.success('Question added successfully!');
      refreshData();  // ✅ Refresh the question table
      onClose();      // ✅ Close the popup      
    } catch (error) {
      // Show error toast
      toast.error('Failed to add question. Please try again.');
    } finally {
      setSaving(false);  // Stop the saving process (loader off)
    }
  };

  // Use React Portal to render modal outside of current DOM hierarchy
  return ReactDOM.createPortal(
    <div className="add-question-modal-overlay" onClick={onClose}>
      <div className="add-question-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="add-question-modal-header">
          <h2>➕ Add New Question</h2>
          <button className="add-question-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        {loading ? (
          <div className="add-question-loading">
            <div className="add-question-spinner"></div>
            <p>Loading...</p>
          </div>
        ) : (
          <>
            {/* Modal Body */}
            <div className="add-question-modal-body">
              <form className="add-question-form">
                {/* Question Text */}
                <div className="add-question-form-group">
                  <label className="add-question-label">
                    <span className="add-question-label-text">Question Text <span className="required">*</span></span>
                    <textarea
                      name="text"
                      value={formData.text || ''}
                      onChange={handleInputChange}
                      rows="4"
                      className="add-question-textarea"
                      placeholder="Enter question text..."
                    />
                  </label>
                </div>

                {/* Segment Dropdown */}
                <div className="add-question-form-group">
                  <label className="add-question-label">
                    <span className="add-question-label-text">Segment <span className="required">*</span></span>
                    {loadingSegments ? (
                      <div className="add-question-loading-segments">
                        <span className="add-question-small-spinner"></span>
                        Loading segments...
                      </div>
                    ) : (
                      <select
                        name="segment_id"
                        value={formData.segment_id || ''}
                        onChange={handleInputChange}
                        disabled={segments.length === 0}
                        className="add-question-select"
                      >
                        <option value="" disabled>
                          Select Segment
                        </option>
                        {segments.map((segment) => (
                          <option key={segment.id} value={segment.id}>
                            {segment.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </label>
                </div>

                {/* Image Upload and Question Type Row */}
                <div className="add-question-form-row">
                  {/* Image Upload */}
                  <div className="add-question-form-group add-question-image-group">
                    <label className="add-question-label">
                      <span className="add-question-label-text">Question Image</span>
                      {formData.image ? (
                        <div className="add-question-image-preview">
                          <img
                            src={formData.image}
                            alt="Question"
                            className="add-question-preview-img"
                          />
                          <button
                            type="button"
                            onClick={() => document.getElementById('add-question-image-upload').click()}
                            className="add-question-update-img-btn"
                          >
                            📷 Update Image
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => document.getElementById('add-question-image-upload').click()}
                          className="add-question-upload-img-btn"
                        >
                          📷 Upload Image
                        </button>
                      )}
                      <input
                        type="file"
                        id="add-question-image-upload"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleImageUpload}
                      />
                    </label>
                  </div>

                  {/* Question Type */}
                  <div className="add-question-form-group">
                    <label className="add-question-label">
                      <span className="add-question-label-text">Question Type <span className="required">*</span></span>
                      <select
                        name="question_type"
                        value={formData.question_type || ''}
                        onChange={handleInputChange}
                        className="add-question-select"
                      >
                        <option value="" disabled>
                          Select Question Type
                        </option>
                        <option value="mcsr">Multiple Choice - Single Response</option>
                        <option value="eitheror">Either-Or</option>
                        <option value="subjective">Subjective</option>
                      </select>
                    </label>
                  </div>
                </div>

                {/* Randomize, Marks, and Level Row */}
                <div className="add-question-form-row-three">
                  {/* Randomize Checkbox */}
                  <div className="add-question-form-group">
                    <label className="add-question-checkbox-label">
                      <input
                        type="checkbox"
                        name="is_randomized"
                        checked={formData.is_randomized || false}
                        onChange={(e) =>
                          setFormData({ ...formData, is_randomized: e.target.checked })
                        }
                        className="add-question-checkbox"
                      />
                      <span className="add-question-checkbox-text">🔀 Randomize</span>
                    </label>
                  </div>

                  {/* Marks */}
                  <div className="add-question-form-group">
                    <label className="add-question-label">
                      <span className="add-question-label-text">Marks <span className="required">*</span></span>
                      <input
                        type="number"
                        name="marks"
                        value={formData.marks || ''}
                        onChange={handleInputChange}
                        className="add-question-input"
                        placeholder="Marks"
                        min="0"
                        step="0.5"
                      />
                    </label>
                  </div>

                  {/* Level */}
                  <div className="add-question-form-group">
                    <label className="add-question-label">
                      <span className="add-question-label-text">Level <span className="required">*</span></span>
                      <select
                        name="level"
                        value={formData.level}
                        onChange={handleInputChange}
                        className="add-question-select"
                      >
                        <option value="easy">Easy</option>
                        <option value="medium">Medium</option>
                        <option value="hard">Hard</option>
                      </select>
                    </label>
                  </div>
                </div>
              </form>
            </div>

            {/* Modal Footer */}
            <div className="add-question-modal-footer">
              <button 
                type="button" 
                onClick={onClose} 
                className="add-question-cancel-btn"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="add-question-save-btn"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span className="add-question-btn-spinner"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    💾 Add Question
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default AddOneQuestion;
